import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types';
import { useCartViewModel } from '../../viewmodels/useCartViewModel';
import { useTheme } from '../../themes/ThemeProvider';
import ProductImagePlaceholder from './ProductImagePlaceholder';

interface ProductCardProps {
  product: Product;
}

const ASPECT_RATIO_CLASS: Record<string, string> = {
  portrait: 'aspect-[3/4]',
  'portrait-110': 'aspect-[10/11]', // ~110% height:width ratio (editorial jewelry)
  square: 'aspect-square',
  '16:9': 'aspect-video',
  landscape: 'aspect-[4/3]',
};

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addItem } = useCartViewModel();
  const navigate = useNavigate();
  const { branding } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const cardConfig = branding?.productCard;
  const cardStyling = cardConfig?.styling;
  const cardTypography = cardConfig?.typography;
  const cardSpacing = cardConfig?.spacing;
  const showAddToCart = cardConfig?.showAddToCart !== false;

  const aspectClass = ASPECT_RATIO_CLASS[cardConfig?.imageAspectRatio || 'portrait'] || 'aspect-[3/4]';

  const defaultVariant = product.variants?.find(variant =>
    variant.calculated_price && variant.calculated_price.calculated_amount > 0
  ) || product.variants?.[0] || null;

  const price = defaultVariant?.calculated_price?.calculated_amount || 0;
  const currency = defaultVariant?.calculated_price?.currency_code || 'sek';

  const formatPrice = (amount: number, currencyCode: string) => {
    const locale = currencyCode.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
    }).format(amount / 100);
  };

  const formattedPrice = price > 0 ? formatPrice(price, currency) : 'Price TBD';
  const showPriceTerm = cardConfig?.priceDisplay === 'prominent-with-term';

  const originalPrice = defaultVariant?.calculated_price?.original_amount;
  const isOnSale = typeof originalPrice === 'number' && originalPrice > price && price > 0;
  const salePill = cardConfig?.salePill;
  const showSalePill = isOnSale && salePill?.enabled !== false;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (defaultVariant && defaultVariant.id) {
      addItem(defaultVariant.id, 1);
    }
  };

  const isAvailable = defaultVariant &&
    defaultVariant.id &&
    (defaultVariant.inventory_quantity === undefined || defaultVariant.inventory_quantity > 0);

  // Card styling from branding config (with sensible defaults)
  const cardStyle = useMemo<React.CSSProperties>(() => ({
    borderRadius: cardStyling?.borderRadius,
    border: cardStyling?.border,
    boxShadow: isHovered ? cardStyling?.hoverShadow : cardStyling?.shadow,
    padding: cardStyling?.padding,
    transition: cardStyling?.transition || 'all 300ms ease',
    backgroundColor: cardStyling?.backgroundColor,
    transform: isHovered && cardStyling?.hoverTranslateY
      ? `translateY(${cardStyling.hoverTranslateY})`
      : undefined,
  }), [cardStyling, isHovered]);

  const imageStyle = useMemo<React.CSSProperties>(() => ({
    transform: isHovered && cardStyling?.imageHoverScale
      ? `scale(${cardStyling.imageHoverScale})`
      : undefined,
    transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
  }), [cardStyling?.imageHoverScale, isHovered]);

  const titleStyle: React.CSSProperties = {
    fontSize: cardTypography?.titleSize,
    fontWeight: cardTypography?.titleWeight,
    color: cardTypography?.titleColor,
    letterSpacing: cardTypography?.titleLetterSpacing,
  };

  const priceStyle: React.CSSProperties = {
    fontSize: cardTypography?.priceSize,
    fontWeight: cardTypography?.priceWeight,
    color: cardTypography?.priceColor || 'var(--color-primary)',
  };

  const contentPadding = cardSpacing?.contentPadding;
  const contentGap = cardSpacing?.gap;

  return (
    <a
      data-testid="product-card"
      href={`/products/${product.id}`}
      onClick={(e) => { e.preventDefault(); navigate(`/products/${product.id}`); }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group bg-[var(--color-bg)] overflow-hidden cursor-pointer border border-border block no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      style={cardStyle}
      aria-label={product.title}
    >
      {/* Product Image — crossfades to secondary image on hover when available.
          Editorial ecommerce pattern (matches carolinesvedbom.com). */}
      <div
        className={`${aspectClass} relative overflow-hidden bg-bg-secondary mb-4`}
        style={cardConfig?.imageCursor === 'zoom-in' ? { cursor: 'zoom-in' } : undefined}
      >
        {showSalePill && (
          <span
            className="absolute top-3 left-3 z-10 text-[0.6875rem] uppercase tracking-wider px-2.5 py-1 pointer-events-none"
            style={{
              backgroundColor: salePill?.backgroundColor || '#F9DEE5',
              color: salePill?.color || '#AF7B88',
              borderRadius: salePill?.borderRadius || '9999px',
            }}
            data-testid="product-sale-pill"
          >
            {salePill?.text || 'Sale'}
          </span>
        )}
        {product.images?.[0] ? (
          <>
            <img
              src={product.images[0].url}
              alt={product.title}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 pointer-events-none"
              style={{
                ...imageStyle,
                opacity: isHovered && product.images[1] ? 0 : 1,
              }}
            />
            {product.images[1] && (
              <img
                src={product.images[1].url}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 pointer-events-none"
                style={{
                  ...imageStyle,
                  opacity: isHovered ? 1 : 0,
                }}
              />
            )}
          </>
        ) : (
          <ProductImagePlaceholder size={48} />
        )}
      </div>

      {/* Product Info */}
      <div
        className="text-center"
        style={{ padding: contentPadding ?? '0 1rem 1rem', gap: contentGap }}
      >
        <h3
          className="text-sm tracking-wide text-text-primary mb-2 transition-colors"
          style={titleStyle}
        >
          {product.title}
        </h3>

        <div className="flex justify-between items-center mt-md">
          <span
            className="text-xl font-semibold"
            style={priceStyle}
            data-testid="product-price"
            aria-label={`Price: ${formattedPrice}`}
          >
            {formattedPrice}
            {showPriceTerm && (
              <span className="text-sm font-normal text-text-secondary ml-1">/ month</span>
            )}
          </span>

          {showAddToCart && (
            <button
              onClick={handleAddToCart}
              disabled={!isAvailable}
              className={`
                px-lg py-sm rounded-sm text-sm font-medium transition-all
                ${isAvailable
                  ? 'bg-primary text-[var(--color-text-on-primary)] hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                  : 'bg-bg-tertiary text-text-light cursor-not-allowed'
                }
              `}
              data-testid="product-card-add-to-cart-button"
              aria-label={`Add ${product.title} to cart`}
            >
              {!defaultVariant ? 'Unavailable' : !isAvailable ? 'Out of Stock' : 'Add to Cart'}
            </button>
          )}
        </div>

        {/* Availability indicator */}
        {isAvailable && (
          <div
            className="mt-sm flex items-center gap-1 text-xs text-text-light"
            role="status"
            aria-live="polite"
          >
            <span className="w-2 h-2 rounded-full bg-success" aria-hidden="true"></span>
            <span>In Stock</span>
          </div>
        )}
      </div>
    </a>
  );
};

export default ProductCard;
